<script lang="ts" setup>
import FireOutbreakCard from '../FireOutbreakCard.vue'
import CustomFireOutbreakMarker from '../CustomFireOutbreakMarker.vue'
import EmptyFireOutbreakList from '../EmptyFireOutbreakList.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import { useRegistryUtils } from '@/composables/useRegistryUtils'
import { inject, ref, watch } from 'vue'
import { FireRiskDashboardViewModel } from '../../viewmodels/FireRiskDashboard.viewmodel'
import { FireOutbreakReactionEnum } from '@/@core/fire-risk-dashboard/domain/enum/FireOutbreakReaction.enum'
import { ShareFireOutbreakAlertEnum } from '../../enums/ShareFireOutbreakAlert.enum'
import { FireOutbreak } from '@/@core/fire-risk-dashboard/domain/entities/FireOutbreak'
import MapBoxMap from '@/@web/ui/map/MapBoxMap.vue'
import MapBoxMarker from '@/@web/ui/map/MapBoxMarker.vue'
import MapBoxZoomControl from '../../../../ui/map/MapBoxZoomControl.vue'
import MapBoxDrawLineControl from '../../../../ui/map/MapBoxDrawLineControl.vue'
import MapBoxFullscreenControl from '../../../../ui/map/MapBoxFullscreenControl.vue'
import MapBoxLayer from '../../../../ui/map/MapBoxLayer.vue'
import MapBoxSource from '../../../../ui/map/MapBoxSource.vue'
import MapInfoToggle from '../../../@shared/components/MapInfoToggle.vue'
import SimpleTextMarker from '../../../../ui/map/SimpleTextMarker.vue'
import { MapFillLayerFactory } from '../../../../ui/map/factories/MapFillLayer.factory'
import { MapOutlineLayerFactory } from '../../../../ui/map/factories/MapOutlineLayer.factory'

const vm = inject<FireRiskDashboardViewModel>('fire-risk-dashboard-viewmodel')!

const {
	pageData: alerts,
	page: alertsPage,
	pageSize: alertsPageSize,
	onPageChange: alertsOnPageChange,
} = useRegistryUtils(vm.mapFireOutbreaks, [])

alertsPageSize.value = 4

function handleReaction(reaction: FireOutbreakReactionEnum, alertId: string) {
	vm.rateFireOutbreak(reaction, alertId)
}

function handleShare(share: ShareFireOutbreakAlertEnum, alert: FireOutbreak) {
	vm.handleShareFireOutbreakAlert(share, alert)
}

const rerender = ref(Math.random())

watch(vm.mapFireOutbreaks, () => (rerender.value = Math.random()), { deep: true })
</script>

<template>
	<div class="dashboard-card-section relative">
		<div class="p-4 flex flex-col justify-between gap-5">
			<div
				style="display: flex"
				class="h-fit flex-col gap-5"
				v-if="alerts?.length"
				:key="rerender"
			>
				<fire-outbreak-card
					v-for="alert in alerts"
					:key="alert.id"
					:alert="alert"
					@reaction="handleReaction($event, alert.id)"
					@shareTo="handleShare($event, alert)"
				/>
			</div>
			<empty-fire-outbreak-list v-else />
			<pagination
				v-if="alerts?.length"
				:key="vm.mapFireOutbreaks.value?.length"
				:total="vm.mapFireOutbreaks.value?.length"
				:page="alertsPage"
				:page-size="alertsPageSize"
				hide-line-count
				@paginationSettings="alertsOnPageChange"
			/>
		</div>
		<map-box-map
			draw
			@load="vm.setMapListeners($event)"
		>
			<div
				:key="rerender"
				v-if="vm.mapInstance.value"
			>
				<map-box-marker
					v-for="alert in vm.mapFireOutbreaks.value"
					:key="alert.id"
					:latitude="alert.latitude"
					:longitude="alert.longitude"
				>
					<custom-fire-outbreak-marker :alert="alert" />
				</map-box-marker>
			</div>

			<template v-if="vm.mapInstance.value && vm.showFarmInfo.value">
				<map-box-marker
					v-for="farm in vm.selectedFarmCentroids.value"
					:key="'farm-label-' + farm.id"
					:latitude="farm.latitude"
					:longitude="farm.longitude"
				>
					<simple-text-marker :text="farm.name" />
				</map-box-marker>
			</template>

			<map-box-zoom-control
				v-if="vm.mapInstance.value"
				position="top-left"
			/>

			<map-box-draw-line-control
				v-if="vm.mapInstance.value"
				position="top-left"
			/>

			<map-box-fullscreen-control
				v-if="vm.mapInstance.value"
				position="top-right"
			/>

			<map-box-source
				v-if="vm.mapInstance.value"
				init-fit-bounds
				id="shape"
				:spec="{
					type: 'geojson',
					data: vm.geojson.value,
				}"
			/>

			<map-box-layer
				v-if="vm.mapInstance.value"
				:spec="MapFillLayerFactory.make()"
			/>
			<map-box-layer
				v-if="vm.mapInstance.value"
				:spec="MapOutlineLayerFactory.make()"
			/>
			<template #overlay>
				<map-info-toggle
					class="absolute top-[10px] right-[52px] z-[2]"
					:value="vm.showFarmInfo.value"
					label="Mostrar informações de fazendas"
					@input="vm.showFarmInfo.value = $event"
				/>
				<div
					class="bg-white-neutral text-s text-black-neutral p-2 rounded shadow-md absolute z-10 bottom-4 right-4"
					v-if="vm.rulerDistanceLabel.value"
				>
					{{ vm.rulerDistanceLabel.value }}
				</div>
			</template>
		</map-box-map>
	</div>
</template>

<style scoped>
.dashboard-card-section {
	height: 624px;
	display: grid;
	grid-template-columns: 1fr 2fr;
}
</style>
